// modules
import { createSelector } from 'reselect'
import { get, forEach, isEmpty } from 'lodash'

// constants
import { ADDITIONAL_GOAL_CATEGORY_LIST } from 'src/constants'

// utils
import { patientSelector } from 'src/store/modules/patient/selectors'

const mapPatientBillingCodes = (codes) => {
  const mappedCodes = []
  delete codes.teleMedicine
  Object.keys(codes).forEach((key) => {
    Object.keys(codes[key]).forEach((uuid, idx) => {
      const code = {
        uuid,
        value: uuid,
        ...codes[key][uuid]
      }
      code.title =
        code.addOn && !isEmpty(code.addOn)
          ? `${code.code} - ${code.description} / ${code.addOn
              .map((addOn) => `${addOn.code} - ${addOn.description}`)
              .join(' / ')}`
          : `${code.code} - ${code.description}`

      if (!idx)
        mappedCodes.push({ title: key.toUpperCase(), disabled: true, key })

      mappedCodes.push(code)
    })
  })
  return mappedCodes
}

const patientCarePlan$ = createSelector(patientSelector, (patient = {}) =>
  get(patient, ['carePlan'], {})
)

export const responsibleList$ = createSelector(
  patientCarePlan$,
  ({ responsibleList = [] }) =>
    responsibleList.map((item, key) => ({
      ...item,
      key,
      title: item.fullName,
      value: item.uuid
    }))
)

export const responsibleListWithEmpty$ = createSelector(
  responsibleList$,
  (responsibleList = []) =>
    [{ fullName: 'None', uuid: null }, ...responsibleList].map((item, key) => ({
      ...item,
      key,
      title: item.fullName,
      value: item.uuid
    }))
)

export const goalList$ = createSelector(patientCarePlan$, ({ goalList = [] }) =>
  goalList.map((item, key) => ({ ...item, key }))
)

// TODO: Say thanks to back-end guys
export const goalCategoryList$ = createSelector(
  patientCarePlan$,
  ({ goalCategoryList = [] }) =>
    goalCategoryList
      .concat(ADDITIONAL_GOAL_CATEGORY_LIST)
      .map((item, key) => ({
        ...item,
        key,
        value: item.uuid || item.type,
        type: item.type === 10 ? 1 : item.type > 100 ? 4 : item.type
      }))
      .sort((a, b) => {
        if (a.title < b.title) {
          return -1
        } else if (a.title > b.title) {
          return 1
        }

        return 0
      })
)

export const goalCategoryListModal$ = createSelector(
  patientCarePlan$,
  ({ goalCategoryList = [] }) =>
    goalCategoryList.map((item, key) => ({
      ...item,
      key,
      value: item.uuid
    }))
)

export const patientGoalList$ = createSelector(
  patientCarePlan$,
  ({ patientGoalList = [] }) =>
    patientGoalList.map((item, key) => ({
      ...item,
      key,
      patientInterventions: get(item, 'patientInterventions', []).map(
        (item, key) => ({ ...item, key })
      )
    }))
)

export const patientModalGoalList$ = createSelector(
  patientCarePlan$,
  ({ patientModalGoalList = [] }) =>
    patientModalGoalList.map((item, key) => ({
      ...item,
      key,
      patientInterventions: get(item, 'patientInterventions', []).map(
        (item, key) => ({ ...item, key })
      )
    }))
)

export const patientGoalValue$ = createSelector(
  patientCarePlan$,
  ({ patientGoalValue = {} }) => patientGoalValue
)

export const interventionList$ = createSelector(
  patientCarePlan$,
  ({ interventionList = [] }) =>
    interventionList.map((item, key) => ({ ...item, key }))
)

export const addActivityInterventionList$ = createSelector(
  patientCarePlan$,
  ({ addActivityInterventionList = [] }) =>
    addActivityInterventionList.map((item, key) => ({
      ...item,
      key
    }))
)

export const interventionTypeList$ = createSelector(
  patientCarePlan$,
  ({ interventionTypeList = [] }) =>
    interventionTypeList.map((item, key) => ({
      ...item,
      key,
      value: item.uuid
    }))
)

export const patientInterventionValue$ = createSelector(
  patientCarePlan$,
  ({ patientInterventionValue = {} }) => patientInterventionValue
)

export const expandedPatientInterventionList$ = createSelector(
  patientSelector,
  (profile) => {
    const expandedPatientInterventionList = get(
      profile,
      'carePlan.expandedPatientInterventionList',
      {}
    )
    const updExpandedPatientInterventionList = {}

    forEach(expandedPatientInterventionList, (interventionList, key) => {
      updExpandedPatientInterventionList[key] = interventionList.map(
        (intervention) => ({
          ...intervention,
          passed: !!+intervention.passed,
          sevenDaysToPass: !!+intervention.sevenDaysToPass
        })
      )
    })

    return updExpandedPatientInterventionList
  }
)

export const patientBillingCodes$ = createSelector(
  patientSelector,
  ({ carePlan }) =>
    mapPatientBillingCodes(get(carePlan, 'patientBillingCodes', {}))
)

export const patientCarePlanReviews$ = createSelector(
  patientSelector,
  ({ carePlan }) => carePlan.patientReviews || []
)

export const patientCarePlanLogs$ = createSelector(
  patientSelector,
  ({ carePlan }) => carePlan.patientLogs || []
)

export const patientActiveCarePlanCount$ = createSelector(
  patientCarePlan$,
  ({ activeCarePlanCount = {} }) => activeCarePlanCount
)
